import { Link } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { 
  Building2, Car, Users, BarChart3, Shield, Clock, 
  CheckCircle2, ArrowRight, CreditCard, Settings,
  FileText, Headphones, TrendingUp, Wallet
} from 'lucide-react';

const BusinessPageEN = () => {
  const solutions = [
    {
      icon: Building2,
      title: 'For Companies',
      description: 'Install charging stations at your workplace. Offer charging as a benefit for employees.',
      features: ['Full installation', 'Maintenance', 'Consumption reports', 'Custom billing']
    },
    {
      icon: Car,
      title: 'Fleet Management',
      description: 'Dedicated platform for electric vehicle fleets. Real-time monitoring and cost optimization.',
      features: ['Central dashboard', 'Scheduled charging', 'Detailed reports', 'Budget allocation']
    },
    {
      icon: Users,
      title: 'For Hotels & Retail',
      description: 'Attract customers with charging stations. Offer charging as a premium additional service.',
      features: ['Quick installation', 'Custom branding', 'POS integration', 'Marketing support']
    }
  ];

  const platformFeatures = [
    {
      icon: BarChart3,
      title: 'Analytics Dashboard',
      description: 'Monitor consumption, costs, and performance in real-time.'
    },
    {
      icon: Wallet,
      title: 'Budget Management',
      description: 'Set budgets for departments or specific vehicles.'
    },
    {
      icon: FileText,
      title: 'Automatic Reports',
      description: 'Detailed invoices and reports automatically every month.'
    },
    {
      icon: Settings,
      title: 'Full Control',
      description: 'Manage access, limits, and charging rules.'
    },
    {
      icon: CreditCard,
      title: 'Flexible Billing',
      description: 'Single invoice for all company charges.'
    },
    {
      icon: Headphones,
      title: 'Dedicated Support',
      description: 'Personal account manager and priority support line.'
    }
  ];

  const benefits = [
    { num: '30%', desc: 'Fuel cost reduction' },
    { num: '24/7', desc: 'Access to AlbMove network' },
    { num: '100%', desc: 'Billing transparency' },
    { num: '€0', desc: 'Installation cost*' }
  ];

  return (
    <div data-testid="business-page-en" className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="section-padding bg-slate-900 text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-0 right-0 w-96 h-96 bg-blue-600 rounded-full blur-3xl" />
          <div className="absolute bottom-0 left-0 w-72 h-72 bg-lime-400 rounded-full blur-3xl" />
        </div>
        
        <div className="container-custom relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm rounded-full px-4 py-2 mb-6">
                <Building2 className="w-4 h-4 text-lime-400" />
                <span className="text-sm font-medium text-slate-300">Business Solutions</span>
              </div>
              
              <h1 
                data-testid="business-title"
                className="text-4xl md:text-6xl font-bold tracking-tight mb-6"
              >
                Electrify<br />
                <span className="text-lime-400">Your Business</span>
              </h1>
              
              <p className="text-xl text-slate-300 leading-relaxed mb-8 max-w-lg">
                Complete charging solutions for companies, fleets, and retail businesses. 
                Reduce costs and contribute to sustainability.
              </p>
              
              <div className="flex flex-wrap gap-4">
                <Button 
                  asChild
                  data-testid="business-cta-contact"
                  className="bg-lime-400 hover:bg-lime-300 text-slate-900 rounded-full px-8 py-6 text-lg font-bold"
                >
                  <Link to="/en/contact">
                    Request a Quote
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </Link>
                </Button>
                <Button 
                  asChild
                  variant="outline"
                  className="bg-transparent border-2 border-white/30 text-white hover:bg-white/10 rounded-full px-8 py-6 text-lg font-bold"
                >
                  <a href="tel:+355682001818">
                    Call Us
                  </a>
                </Button>
              </div>
            </div>
            
            {/* Stats */}
            <div className="grid grid-cols-2 gap-6">
              {benefits.map((benefit, index) => (
                <div 
                  key={index}
                  className="bg-white/5 backdrop-blur-sm rounded-2xl p-6 border border-white/10"
                >
                  <p className="text-4xl font-bold text-lime-400 mb-2">{benefit.num}</p>
                  <p className="text-slate-300">{benefit.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Solutions */}
      <section data-testid="solutions" className="section-padding bg-white">
        <div className="container-custom">
          <div className="text-center mb-16">
            <span className="inline-block text-sm font-semibold uppercase tracking-widest text-blue-600 mb-4">
              Our Solutions
            </span>
            <h2 className="text-4xl md:text-5xl font-bold text-slate-900 tracking-tight">
              Solutions for Every Business
            </h2>
          </div>
          
          <div className="grid lg:grid-cols-3 gap-8">
            {solutions.map((solution, index) => (
              <div 
                key={index}
                data-testid={`solution-${index}`}
                className="bg-slate-50 rounded-3xl p-8 hover:bg-blue-50 transition-colors group"
              >
                <div className="w-16 h-16 rounded-2xl bg-blue-100 group-hover:bg-blue-600 flex items-center justify-center mb-6 transition-colors">
                  <solution.icon className="w-8 h-8 text-blue-600 group-hover:text-white transition-colors" />
                </div>
                <h3 className="text-2xl font-bold text-slate-900 mb-3">{solution.title}</h3>
                <p className="text-slate-500 leading-relaxed mb-6">{solution.description}</p>
                <ul className="space-y-2">
                  {solution.features.map((feature, fIndex) => (
                    <li key={fIndex} className="flex items-center gap-2 text-sm text-slate-600">
                      <CheckCircle2 className="w-4 h-4 text-lime-500 flex-shrink-0" />
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Platform Features */}
      <section data-testid="platform" className="section-padding bg-slate-50">
        <div className="container-custom">
          <div className="text-center mb-16">
            <span className="inline-block text-sm font-semibold uppercase tracking-widest text-blue-600 mb-4">
              Platform
            </span>
            <h2 className="text-4xl md:text-5xl font-bold text-slate-900 tracking-tight mb-4">
              Powerful Business Dashboard
            </h2>
            <p className="text-lg text-slate-500 max-w-2xl mx-auto">
              Manage all company charges from a single platform.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {platformFeatures.map((feature, index) => (
              <div 
                key={index}
                className="bg-white rounded-2xl p-6 border border-slate-200/50 hover:shadow-lg transition-all"
              >
                <feature.icon className="w-10 h-10 text-blue-600 mb-4" />
                <h3 className="text-lg font-bold text-slate-900 mb-2">{feature.title}</h3>
                <p className="text-slate-500 text-sm">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Fleet Management */}
      <section className="section-padding bg-gradient-to-br from-blue-600 to-blue-700 text-white">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <span className="inline-block text-sm font-semibold uppercase tracking-widest text-lime-400 mb-4">
                Fleet Management
              </span>
              <h2 className="text-4xl md:text-5xl font-bold tracking-tight mb-6">
                Switch to Electric Fleet Without Stress
              </h2>
              <p className="text-xl text-blue-100 leading-relaxed mb-8">
                We take care of everything - from infrastructure planning to driver training. 
                You focus on your business.
              </p>
              
              <ul className="space-y-4 mb-8">
                {[
                  'Fleet needs analysis',
                  'Charging network planning',
                  'Depot station installation',
                  'Staff training',
                  '24/7 monitoring and maintenance'
                ].map((item, index) => (
                  <li key={index} className="flex items-center gap-3">
                    <CheckCircle2 className="w-6 h-6 text-lime-400 flex-shrink-0" />
                    <span className="text-blue-100 text-lg">{item}</span>
                  </li>
                ))}
              </ul>
              
              <Button 
                asChild
                className="bg-lime-400 hover:bg-lime-300 text-slate-900 rounded-full px-8 py-6 text-lg font-bold"
              >
                <Link to="/en/contact">
                  Start Free Consultation
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Link>
              </Button>
            </div>
            
            <div className="relative">
              <img 
                src="https://images.unsplash.com/photo-1622399126461-cde3bb1255fd?crop=entropy&cs=srgb&fm=jpg&w=600&q=80"
                alt="Electric fleet"
                className="rounded-3xl shadow-2xl"
              />
              <div className="absolute -bottom-6 -left-6 bg-white text-slate-900 rounded-2xl p-6 shadow-xl">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl bg-lime-100 flex items-center justify-center">
                    <TrendingUp className="w-6 h-6 text-lime-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">30%</p>
                    <p className="text-sm text-slate-500">Cost Savings</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <div className="bg-slate-900 rounded-3xl p-12 md:p-16 text-center text-white relative overflow-hidden">
            <div className="absolute top-0 right-0 w-64 h-64 bg-blue-600/20 rounded-full blur-3xl" />
            <div className="absolute bottom-0 left-0 w-48 h-48 bg-lime-400/20 rounded-full blur-3xl" />
            
            <div className="relative z-10">
              <h2 className="text-3xl md:text-5xl font-bold mb-6">
                Ready to Electrify Your Business?
              </h2>
              <p className="text-lg text-slate-300 mb-8 max-w-xl mx-auto">
                Contact our team for a free consultation and personalized quote.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button 
                  asChild
                  className="bg-lime-400 hover:bg-lime-300 text-slate-900 rounded-full px-8 py-6 text-lg font-bold"
                >
                  <Link to="/en/contact">
                    Request a Quote
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </Link>
                </Button>
                <Button 
                  asChild
                  variant="outline"
                  className="bg-transparent border-2 border-white/30 text-white hover:bg-white/10 rounded-full px-8 py-6 text-lg font-bold"
                >
                  <a href="tel:+355682001818">
                    +355 68 200 1818
                  </a>
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default BusinessPageEN;
